import type { ApplicationType } from '@repo/api';

/**
 * FALLBACK limit, used only when the API doesn't serve one.
 *
 * The authority is `applicant.activeApplicationLimit`, which comes from the same
 * constant the submit guard enforces. Always resolve through
 * `resolveActiveApplicationLimit` rather than reading this directly: hardcoding
 * the number here is how the client and the guard drift apart (they did — a
 * backend lowered to 1 kept letting people apply against a client that still
 * believed in 10).
 */
export const ACTIVE_APPLICATION_LIMIT_FALLBACK = 10;

/** The enforced ceiling, preferring what the API served. */
export function resolveActiveApplicationLimit(limit: number | undefined | null): number {
  if (typeof limit !== 'number' || Number.isNaN(limit) || limit <= 0) {
    return ACTIVE_APPLICATION_LIMIT_FALLBACK;
  }
  return limit;
}

/**
 * True when the renter's DRAFTS PLUS ACTIVE applications reach the ceiling.
 *
 * Drives the soft "limit reminder": drafts themselves are never capped, but
 * once drafts + active reach the limit the renter cannot submit them all, so
 * the reminder states the ceiling before they invest in another one. Counting
 * drafts alone would stay silent for someone holding 9 active and 1 draft —
 * who is one submit away from the wall.
 *
 * A missing `activeCount` counts as 0, so an unknown count shows fewer
 * reminders rather than warning on a number we don't have.
 */
export function isAtCombinedApplicationLimit(
  draftCount: number,
  activeCount: number | undefined | null,
  limit: number | undefined | null,
): boolean {
  const active = typeof activeCount === 'number' && !Number.isNaN(activeCount) ? activeCount : 0;
  return draftCount + active >= resolveActiveApplicationLimit(limit);
}

/**
 * True when the renter has no active slot left.
 *
 * `count` comes from `applicant.activeApplicationCount`. When it's missing —
 * an older API, or a failed fetch — this returns false: the UI **fails open**
 * so we never wrongly block someone from applying. The backend's submit guard
 * is what actually enforces the limit, so failing open costs nothing.
 */
export function isAtActiveApplicationLimit(
  count: number | undefined | null,
  limit: number | undefined | null,
): boolean {
  if (typeof count !== 'number' || Number.isNaN(count)) return false;
  return count >= resolveActiveApplicationLimit(limit);
}

/**
 * The renter's own draft applications that are tied to a unit.
 *
 * Unit-less rows are excluded: those are applicant-profile leftovers, not
 * applications the renter started for a place, and they must not push anyone
 * over the draft reminder threshold.
 */
export function countUnitDrafts(applications: ApplicationType[]): number {
  return applications.filter(a => Boolean(a.unitId) && isDraftStatus(a.status)).length;
}

/** The application this renter already has for `unitId`, if any. */
export function findApplicationForUnit(
  applications: ApplicationType[],
  unitId: string | null | undefined,
): ApplicationType | null {
  if (!unitId) return null;
  return applications.find(a => a.unitId === unitId) ?? null;
}

/**
 * A draft is anything the renter can still edit before submitting.
 *
 * `in_progress` covers both a never-submitted draft and a submitted case the
 * renter re-opened — the backend serves both as `in_progress` via
 * `renter_status`, and for the unit page's purposes they behave the same.
 */
export function isDraftStatus(status?: string | null): boolean {
  const s = (status ?? '').toLowerCase();
  return s === 'in_progress' || s === 'in progress' || s === 'draft' || s === '';
}

/**
 * "1 active application" / "2 active applications".
 *
 * The design only ever shows the plural (the limit is 10), but the same strings
 * render at any count — and did read "You have 1 active applications" once the
 * ceiling was lowered for testing.
 */
export function activeApplicationsPhrase(count: number): string {
  return `${count} active application${count === 1 ? '' : 's'}`;
}
